import { Navigate, useLocation, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetCertificationQuery,
  useGetParticipantsQuery,
} from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import type {
  AdminOriginState,
  AdminTab,
} from "@/domains/certifications/types";
import {
  ADMIN_TABS,
  certificationPaths,
} from "@/domains/certifications/routes";
import ParticipantDetail from "@/features/certifications/participant-detail";
import AdminSkeleton from "@/features/certifications/admin/components/AdminSkeleton";

const isAdminTab = (value: unknown): value is AdminTab =>
  Object.values(ADMIN_TABS).includes(value as AdminTab);

const ParticipantDetailPage = () => {
  const { slug, userId } = useParams();
  const { state } = useLocation();
  const fromTab = (state as Partial<AdminOriginState> | null)?.fromTab;
  const backTab = isAdminTab(fromTab) ? fromTab : ADMIN_TABS.PARTICIPANTS;
  const user = Number(userId);
  const hasValidUser = Number.isInteger(user) && user > 0;

  const certification = useGetCertificationQuery(slug ?? skipToken);
  const certificationSlug = certification.data?.slug;
  const participants = useGetParticipantsQuery(
    certificationSlug && hasValidUser ? { certificationSlug, user } : skipToken,
  );

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  const backToList = `${certificationPaths.admin(slug)}?tab=${ADMIN_TABS.PARTICIPANTS}`;
  if (!hasValidUser || certification.isError || participants.isError) {
    return <Navigate to={backToList} replace />;
  }

  if (!certification.data || !participants.data) {
    return <AdminSkeleton />;
  }

  const rows = isPaginatedResponse(participants.data)
    ? participants.data.results
    : participants.data;
  const participant = rows[0];
  if (!participant) {
    return <Navigate to={backToList} replace />;
  }

  return (
    <ParticipantDetail
      certification={certification.data}
      participant={participant}
      backTab={backTab}
    />
  );
};

export default ParticipantDetailPage;
